// src/components/AccumulatePointsModal.jsx

import React, { useState, useEffect } from 'react';
import { get, patch, post } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FaCog, FaSpinner } from 'react-icons/fa';
import OfertaValidator from '../ofertas/OfertaValidator';
import OfertaUsageTracker from '../ofertas/OfertaUsageTracker';
import useAuthStore from '../../store/authStore';
import { usePointsEngine } from '../../hooks/usePointsEngine';

const AccumulatePointsModal = ({
  ticketId,
  montoConsumido,
  setMontoConsumido,
  puntosMaximos,
  puntosAcumulados,
  onClose,
  onAccumulate,
  tipoFidelizacion,
  clienteId,
  productos,
  tiendaId, // ✅ NUEVO PARÁMETRO REQUERIDO
  marca, // ✅ NUEVO PARÁMETRO PARA MOTOR DE PUNTOS
}) => {
  // Obtener información del usuario autenticado para identificar el tipo
  const { user, trabajador, franquiciado } = useAuthStore();

  // Función para obtener el símbolo de moneda
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'PEN': 'S/',
      'CLP': '$'
    };
    return symbols[currency] || '$'; // USD como valor por defecto
  };
  const [selectedPoints, setSelectedPoints] = useState(1);
  const [puntosDisponibles, setPuntosDisponibles] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscounts, setSelectedDiscounts] = useState([]);
  const [totalWithDiscount, setTotalWithDiscount] = useState(0);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [selectedOferta, setSelectedOferta] = useState(null);
  const [ofertaValidation, setOfertaValidation] = useState(null);
  const [ofertaUsage, setOfertaUsage] = useState(null);

  // Estados para el motor de puntos automático
  const [calculatedPoints, setCalculatedPoints] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [userModifiedPoints, setUserModifiedPoints] = useState(false);

  // Hook para motor de puntos (especialmente para PUNTOS con límites)
  const ticketData = {
    puntos: puntosAcumulados,
    tipoFidelizacion: tipoFidelizacion
  };

  const {
    motorActivo: motorActivoHook,
    puntosDisponibles: puntosDisponiblesTicket,
    preview: previewCalculation,
    loading: loadingPreview,
    calcularPreview: calcularPreviewHook,
    tieneLimiteAplicado,
    puntosPerdidos: puntosPerdidosHook,
    ticketCompleto
  } = usePointsEngine(ticketData, marca, tipoFidelizacion, ticketId, productos);

  useEffect(() => {
    if (tipoFidelizacion === 'PUNTOS') {
      const maxPuntosDisponibles = puntosMaximos - puntosAcumulados;
      const opciones = Array.from({ length: maxPuntosDisponibles }, (_, i) => i + 1);
      setPuntosDisponibles(opciones);
    }
  }, [puntosMaximos, puntosAcumulados, tipoFidelizacion]);

  // Configurar motor automático si está disponible
  useEffect(() => {
    if (marca && marca.pointsEngineVersion === 1 && marca.pointsRules) {
      // El motor automático está siempre activo si está configurado
      calculatePointsAutomatically();
      // Inicializar selectedPoints con el valor calculado si está disponible
      if (montoConsumido > 0) {
        const initialPoints = simulatePointsCalculation(montoConsumido, marca.pointsRules);
        setSelectedPoints(initialPoints);
      }
    }
  }, [marca]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const total = selectedProducts.reduce((acc, curr) => {
        const producto = productos.find((p) => p._id === curr.productId);
        return acc + (producto?.precio || 0) * curr.cantidad;
      }, 0);
      // Redondear a 2 decimales para evitar problemas de precisión de punto flotante
      const totalRedondeado = Math.round(total * 100) / 100;
      setMontoConsumido(totalRedondeado);
    } else {
      setMontoConsumido(0);
    }
  }, [selectedProducts, productos, setMontoConsumido]);

  useEffect(() => {
    fetchDiscounts();
    fetchOfertas();
  }, []);

  useEffect(() => {
    updateTotalWithDiscount();
  }, [selectedProducts, selectedDiscounts, montoConsumido, selectedOferta, ofertaValidation]);

  // Calcular puntos automáticamente cuando cambia el monto
  useEffect(() => {
    if (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && montoConsumido > 0 && !userModifiedPoints) {
      // Para PUNTOS con motor activo, usar el hook que llama al endpoint
      if (tipoFidelizacion === 'PUNTOS' && motorActivoHook) {
        calcularPreviewHook(montoConsumido, selectedProducts).then((preview) => {
          if (preview) {
            const puntosCalculados = preview.points || 0;
            setCalculatedPoints(puntosCalculados);
            setSelectedPoints(puntosCalculados);
          }
        });
      } else {
        // Para ACUMULACION o sin motor, usar cálculo local (comportamiento existente)
        calculatePointsAutomatically();
        const newPoints = simulatePointsCalculation(montoConsumido, marca.pointsRules);
        setSelectedPoints(newPoints);
      }
    }
  }, [montoConsumido, selectedProducts, marca, userModifiedPoints, tipoFidelizacion, motorActivoHook, calcularPreviewHook]);

  useEffect(() => {
    // Limpiar validación cuando se cambia la oferta seleccionada
    if (!selectedOferta) {
      setOfertaValidation(null);
    }
  }, [selectedOferta]);

  useEffect(() => {
    if (productos) {
      const filtered = productos.filter(producto =>
        producto.nombre.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        producto.descripcion?.toLowerCase().includes(productSearchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [productos, productSearchTerm]);


  const fetchDiscounts = async () => {
    try {
      const response = await get(`rewards/available/${clienteId}`);

      // 🔹 Filtrar solo los descuentos antes de actualizar el estado
      const filteredDiscounts = response.filter(reward => {
        if (!reward.recompensa) return false; // Evitar errores si no hay recompensa

        const now = new Date();
        const isActive = reward.recompensa.activo ?? true;
        const isNotExpired =
          !reward.recompensa.fechaCaducidad || new Date(reward.recompensa.fechaCaducidad) > now;
        const isDiscountType =
          reward.recompensa.tipo?.trim().toUpperCase() === "DESCUENTO";

        return isActive && isNotExpired && isDiscountType;
      });

      setDiscounts(filteredDiscounts);
    } catch (error) {
      Swal.fire("Error", "No se pudieron cargar los descuentos.", "error");
    }
  };

  const fetchOfertas = async () => {
    try {
      const response = await get('ofertas');
      // Filtrar solo ofertas activas
      const ofertasActivas = response.filter(oferta =>
        oferta.estado === 'ACTIVA' &&
        new Date(oferta.fechaInicio) <= new Date() &&
        new Date(oferta.fechaFin) >= new Date()
      );
      setOfertas(ofertasActivas);
    } catch (error) {
      console.error('Error al cargar ofertas:', error);
    }
  };

  // Función para calcular puntos automáticamente
  const calculatePointsAutomatically = async () => {
    if (!marca || !marca.pointsRules || montoConsumido <= 0) return;

    setIsCalculating(true);
    try {
      // Simular cálculo local (en producción esto vendría del backend)
      const points = simulatePointsCalculation(montoConsumido, marca.pointsRules);
      setCalculatedPoints(points);
    } catch (error) {
      console.error('Error calculando puntos:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // Simulación local del cálculo de puntos
  const simulatePointsCalculation = (amount, rules) => {
    const { mode, ratePerAmount, tiers, multiplierByTag, rounding } = rules;

    let points = 0;

    switch (mode) {
      case 'RATE_PER_AMOUNT':
        const { rate, step } = ratePerAmount;
        // CORRECCIÓN: Para "1 punto por euro" con step=1, rate=1
        // Si amount=5, step=1, rate=1 → points = Math.floor(5/1) * 1 = 5
        points = Math.floor(amount / step) * rate;
        break;

      case 'TIERED':
        if (tiers && tiers.length > 0) {
          const tier = tiers.find(t => amount >= t.min && (!t.max || amount <= t.max));
          if (tier) {
            points = Math.floor(amount * tier.rate);
          }
        }
        break;

      case 'MULTIPLIER_BY_TAG':
        if (multiplierByTag) {
          points = Math.floor(amount * multiplierByTag.baseRate);
        }
        break;

      default:
        points = 0;
    }

    // Aplicar redondeo
    switch (rounding) {
      case 'FLOOR':
        points = Math.floor(points);
        break;
      case 'CEIL':
        points = Math.ceil(points);
        break;
      case 'ROUND':
        points = Math.round(points);
        break;
      default:
      // NONE - no hacer nada
    }

    return Math.max(points, 0);
  };

  const handleMontoChange = (e) => {
    let value = e.target.value.replace(',', '.');
    const numValue = Number(value);
    // Redondear a 2 decimales si el valor es válido
    if (!isNaN(numValue) && numValue >= 0) {
      // Permitir escribir decimales, pero redondear al final
      setMontoConsumido(numValue);
    } else if (value === '' || value === '.') {
      // Permitir campo vacío o punto mientras se escribe
      setMontoConsumido(0);
    }
  };

  const handlePointsChange = (e) => {
    const newValue = Number(e.target.value);

    // Para PUNTOS con motor activo, validar límite
    if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1 && puntosDisponiblesTicket !== Infinity) {
      if (newValue > puntosDisponiblesTicket) {
        Swal.fire({
          title: 'Límite Excedido',
          text: `No puedes acumular más de ${puntosDisponiblesTicket} puntos. El ticket tiene un límite de ${puntosMaximos} puntos.`,
          icon: 'warning',
          confirmButtonText: 'Aceptar'
        });
        setSelectedPoints(puntosDisponiblesTicket);
        return;
      }
    }

    setSelectedPoints(newValue);

    // Si el usuario modifica manualmente, marcar como modificado
    if (marca && marca.pointsEngineVersion === 1 && marca.pointsRules) {
      setUserModifiedPoints(true);
    }
  };

  const handleProductSelection = (productId) => {
    setSelectedProducts((prevSelected) => {
      const existingProduct = prevSelected.find((p) => p.productId === productId);
      if (existingProduct) {
        // Si el producto ya está seleccionado, lo deselecciona
        return prevSelected.filter((p) => p.productId !== productId);
      } else {
        // Si no está seleccionado, lo agrega con cantidad 1 por defecto
        return [...prevSelected, { productId, cantidad: 1 }];
      }
    });
  };

  const handleProductQuantityChange = (productId, cantidad) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.map((p) =>
        p.productId === productId ? { ...p, cantidad: Number(cantidad) } : p
      )
    );
  };

  const handleDiscountSelection = (discountId) => {
    const discount = discounts.find((d) => d._id === discountId);
    if (!discount) return;

    const { montoMinimo = 0 } = discount.recompensa;

    if (montoMinimo > montoConsumido) {
      Swal.fire("Restricción no cumplida", `El monto mínimo para este descuento es $${montoMinimo.toFixed(2)}`, "warning");
      return;
    }

    setSelectedDiscounts((prev) =>
      prev.includes(discountId)
        ? prev.filter((id) => id !== discountId)
        : [...prev, discountId]
    );
  };


  const handleAccumulate = async () => {
    // Validar si el ticket está completo (solo para PUNTOS)
    if (tipoFidelizacion === 'PUNTOS' && ticketCompleto()) {
      Swal.fire({
        title: 'Ticket Completo',
        text: 'Este ticket ya ha alcanzado el máximo de puntos.',
        icon: 'warning',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    const selectedRewardCodes = selectedDiscounts.map(discountId => {
      const discount = discounts.find(d => d._id === discountId);
      return discount ? discount._id : null;
    }).filter(Boolean);

    // Construir el array de productos con nombre y precio incluidos
    const formattedProducts = selectedProducts.map(product => {
      const productData = productos.find(p => p._id === product.productId);
      return {
        nombre: productData?.nombre || "Producto sin nombre",
        cantidad: product.cantidad,
        precio: productData?.precio || 0,
        descripcion: productData?.descripcion || "",
      };
    });

    // Determinar puntos a usar (calculados automáticamente o modificados por usuario)
    let pointsToUse = (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && !userModifiedPoints)
      ? calculatedPoints
      : selectedPoints;

    // Para PUNTOS con motor activo, validar límites antes de acumular
    if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1 && puntosDisponiblesTicket !== Infinity) {
      // Verificar si se perderán puntos
      const puntosPerdidos = puntosPerdidosHook();

      if (puntosPerdidos > 0) {
        const result = await Swal.fire({
          title: 'Advertencia',
          html: `Se perderán <strong>${puntosPerdidos}</strong> puntos por superar el límite del ticket.<br>¿Deseas continuar?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, continuar',
          cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // Ajustar puntos según límite disponible
      pointsToUse = Math.min(pointsToUse, puntosDisponiblesTicket);
    }

    try {
      // Aplicar oferta si está seleccionada y validada
      if (selectedOferta && ofertaValidation?.valida) {
        await post('ofertas/use', {
          ofertaId: selectedOferta._id,
          clienteId: clienteId,
          importeCompra: montoConsumido,
          canalUso: 'TICKET',
          codigoTransaccion: `TXN-${ticketId}-${Date.now()}`
        });
      }

      // Determinar el tipo de usuario que está acumulando puntos
      const userType = franquiciado ? 'franquiciado' : trabajador ? 'trabajador' : 'propietario';
      const userId = franquiciado?.franquiciadoId || trabajador?.trabajadorId || user?.userId;

      let ticketActualizado = null;

      if (selectedRewardCodes.length > 0) {
        // Si hay descuentos, aplicar los descuentos antes de acumular puntos
        ticketActualizado = await patch(`tickets/${ticketId}/apply-discount`, {
          ticketId,
          clienteId,
          descuentos: selectedRewardCodes,
          monto: montoConsumido, // Monto original sin descuento
          montoConDescuento: totalWithDiscount, // Monto después del descuento
          puntos: pointsToUse,
          productos: formattedProducts, // Enviar productos con nombre y precio
          tiendaId, // ✅ NUEVO CAMPO REQUERIDO
          userType, // ✅ Tipo de usuario: 'propietario' | 'trabajador' | 'franquiciado'
          userId, // ✅ ID del usuario que realiza la acumulación
          // auto: removido porque el backend no lo acepta
        });
      } else {
        // Si NO hay descuentos, solo acumular puntos normalmente
        ticketActualizado = await patch(`tickets/${ticketId}/accumulate`, {
          ticketId, // Asegurar que se envía correctamente
          monto: montoConsumido, // Monto sin descuentos
          puntos: pointsToUse,
          productos: formattedProducts, // Enviar productos con nombre y precio
          tiendaId, // ✅ NUEVO CAMPO REQUERIDO
          userType, // ✅ Tipo de usuario: 'propietario' | 'trabajador' | 'franquiciado'
          userId, // ✅ ID del usuario que realiza la acumulación
          // auto: removido porque el backend no lo acepta
        });
      }

      // Obtener calcMeta de la respuesta si está disponible (para PUNTOS con motor)
      let calcMeta = null;
      if (ticketActualizado && ticketActualizado.historialConsumiciones && ticketActualizado.historialConsumiciones.length > 0) {
        const ultimaConsumicion = ticketActualizado.historialConsumiciones[ticketActualizado.historialConsumiciones.length - 1];
        calcMeta = ultimaConsumicion?.calcMeta || null;
      }

      // Notificar éxito con información detallada para PUNTOS
      if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1) {
        const puntosPerdidos = calcMeta?.puntosExtrasPerdidos || puntosPerdidosHook() || 0;
        const puntosAjustados = calcMeta?.puntosAjustados || pointsToUse;
        const puntosOriginales = calcMeta?.puntosOriginales || calculatedPoints;
        const limiteSuperado = calcMeta?.limiteSuperado || (puntosPerdidos > 0);

        if (limiteSuperado && puntosPerdidos > 0) {
          Swal.fire({
            title: 'Puntos Acumulados',
            html: `
              <p>Puntos acumulados: <strong>${puntosAjustados}</strong></p>
              ${puntosOriginales !== puntosAjustados ? `<p class="text-gray-600 text-sm">Puntos calculados originalmente: ${puntosOriginales}</p>` : ''}
              <p class="text-yellow-600 mt-2">⚠️ Puntos perdidos: ${puntosPerdidos}</p>
              ${puntosAjustados >= puntosDisponiblesTicket ? '<p class="text-sm text-green-600 mt-2">✅ El ticket está completo.</p>' : ''}
            `,
            icon: 'success',
            confirmButtonText: 'Aceptar'
          });
        } else {
          Swal.fire({
            title: 'Éxito',
            text: `Puntos calculados automáticamente: ${puntosAjustados} puntos acumulados.`,
            icon: 'success',
            confirmButtonText: 'Aceptar'
          });
        }
      } else {
        // Mensaje estándar para ACUMULACION o sin motor
        const successMessage = (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && !userModifiedPoints)
          ? `Puntos calculados automáticamente: ${calculatedPoints} puntos acumulados.`
          : userModifiedPoints
            ? `Puntos modificados manualmente: ${selectedPoints} puntos acumulados.`
            : "Puntos acumulados correctamente.";

        Swal.fire("Éxito", successMessage, "success");
      }

      onAccumulate(pointsToUse, selectedProducts, selectedDiscounts, totalWithDiscount);
    } catch (error) {
      console.error("Error al acumular puntos:", error);
      Swal.fire("Error", "No se pudieron procesar los puntos o descuentos.", "error");
    }
  };

  const updateTotalWithDiscount = () => {
    let total = montoConsumido;

    // Aplicar descuentos de recompensas
    selectedDiscounts.forEach(discountId => {
      const discount = discounts.find(d => d._id === discountId);
      if (discount) {
        const { cantidadMonetaria = 0, porcentaje = 0 } = discount.recompensa;
        total -= cantidadMonetaria;
        total -= (total * porcentaje) / 100;
      }
    });

    // Aplicar oferta si está seleccionada y validada
    if (selectedOferta && ofertaValidation?.valida) {
      switch (selectedOferta.tipo) {
        case 'DESCUENTO_PORCENTAJE':
          total -= (total * selectedOferta.porcentajeDescuento) / 100;
          break;
        case 'DESCUENTO_FIJO':
          total -= selectedOferta.descuentoFijo;
          break;
        case 'ENVIO_GRATIS':
          // El envío gratis no afecta el total de productos
          break;
        case '2X1':
          // Para 2x1, se calcula el descuento del producto más barato
          if (selectedProducts.length > 0) {
            const productosOrdenados = selectedProducts
              .map(p => ({ ...p, precio: productos.find(prod => prod._id === p.productId)?.precio || 0 }))
              .sort((a, b) => a.precio - b.precio);
            const descuento2x1 = productosOrdenados[0]?.precio || 0;
            total -= descuento2x1;
          }
          break;
      }
    }

    setTotalWithDiscount(Math.max(total, 0)); // Evitar valores negativos
  };


  return (
    <div className="fixed z-50 inset-0 overflow-y-auto flex items-center justify-center p-2 sm:p-4">
      {/* Fondo oscuro */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose}></div>

      {/* Contenido del modal */}
      <div className="bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-2xl max-h-[95vh] flex flex-col relative z-20">
        {/* Header del modal */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 py-4 sm:py-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-white">Acumular Puntos</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors duration-200 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido del modal - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Selección de Productos */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Selecciona Productos</h3>
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                {filteredProducts.length} de {productos?.length || 0} productos
                {selectedProducts.length > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    • {selectedProducts.length} seleccionados
                  </span>
                )}
              </div>
            </div>

            {/* Barra de búsqueda */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Lista de productos */}
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
              {filteredProducts && filteredProducts.length > 0 ? (
                <div className="p-3 space-y-2">
                  {filteredProducts.map((producto) => {
                    const isSelected = selectedProducts.find(
                      (p) => p.productId === producto._id
                    );
                    return (
                      <div
                        key={producto._id}
                        className={`flex items-center p-3 rounded-lg transition-all duration-200 border ${isSelected
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                          }`}
                      >
                        <input
                          type="checkbox"
                          id={`product-${producto._id}`}
                          checked={isSelected ? true : false}
                          onChange={() => handleProductSelection(producto._id)}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <label
                            htmlFor={`product-${producto._id}`}
                            className="block font-medium text-gray-900 cursor-pointer truncate text-sm sm:text-base"
                          >
                            {producto.nombre}
                          </label>
                          {producto.descripcion && (
                            <p className="text-xs sm:text-sm text-gray-500 truncate mt-1">
                              {producto.descripcion}
                            </p>
                          )}
                          <p className="text-sm sm:text-base font-semibold text-green-600 mt-1">
                            {getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}{producto.precio.toFixed(2)}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="flex items-center space-x-2 ml-3">
                            <label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">Cantidad:</label>
                            <input
                              type="number"
                              min="1"
                              max="99"
                              value={isSelected.cantidad}
                              onChange={(e) =>
                                handleProductQuantityChange(producto._id, e.target.value)
                              }
                              className="w-16 p-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 px-4">
                  <div className="text-gray-400 mb-3">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm sm:text-base">
                    {productSearchTerm ? 'No se encontraron productos' : 'No hay productos disponibles'}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 mt-1">
                    {productSearchTerm ? 'Intenta con otros términos de búsqueda' : 'Los productos aparecerán aquí cuando estén disponibles'}
                  </p>
                </div>
              )}
            </div>

            {/* Resumen de productos seleccionados */}
            {selectedProducts.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-3 text-sm sm:text-base">Productos Seleccionados:</h4>
                <div className="space-y-2">
                  {selectedProducts.map((selected) => {
                    const producto = productos.find(p => p._id === selected.productId);
                    return (
                      <div key={selected.productId} className="flex justify-between items-center text-sm">
                        <span className="text-blue-800 font-medium">
                          {producto?.nombre} x{selected.cantidad}
                        </span>
                        <span className="font-semibold text-blue-900">
                          {getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}{((producto?.precio || 0) * selected.cantidad).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Descuentos Disponibles */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Descuentos Disponibles</h3>
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                {discounts.length} descuentos disponibles
                {selectedDiscounts.length > 0 && (
                  <span className="ml-2 text-green-600 font-medium">
                    • {selectedDiscounts.length} seleccionados
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
              {discounts.length > 0 ? (
                <div className="p-3 space-y-2">
                  {discounts.map((discount) => {
                    const { _id, recompensa } = discount;
                    const {
                      nombre,
                      cantidadMonetaria = 0,
                      porcentaje = 0,
                      fechaCaducidad,
                      currency = "EUR",
                      montoMinimo = 0,
                    } = recompensa;

                    return (
                      <div
                        key={_id}
                        className={`flex flex-col p-3 rounded-lg transition-all duration-200 border ${selectedDiscounts.includes(_id)
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                          } ${montoMinimo > montoConsumido ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`discount-${_id}`}
                            checked={selectedDiscounts.includes(_id)}
                            onChange={() => handleDiscountSelection(_id)}
                            className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            disabled={montoMinimo > montoConsumido}
                          />

                          <div className="flex-1 min-w-0">
                            <label
                              htmlFor={`discount-${_id}`}
                              className={`block font-medium cursor-pointer text-sm sm:text-base ${montoMinimo > montoConsumido ? 'text-gray-400' : 'text-gray-900'
                                }`}
                            >
                              {nombre}
                            </label>
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              {cantidadMonetaria > 0
                                ? `💰 ${getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}${cantidadMonetaria.toFixed(2)}`
                                : `💸 ${porcentaje}% Descuento`}
                            </p>
                          </div>
                        </div>

                        {/* Monto mínimo requerido */}
                        {montoMinimo > 0 && (
                          <p className={`text-xs sm:text-sm ml-7 mt-2 ${montoMinimo > montoConsumido ? 'text-red-500' : 'text-gray-600'
                            }`}>
                            * Monto mínimo: {getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}{montoMinimo.toFixed(2)}
                            {montoMinimo > montoConsumido && ' (No cumple el requisito)'}
                          </p>
                        )}

                        {/* Fecha de caducidad si está cerca */}
                        {fechaCaducidad && (
                          <p className={`text-xs sm:text-sm ml-7 mt-1 ${new Date(fechaCaducidad) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                            ? "text-red-500"
                            : "text-gray-500"
                            }`}>
                            ⏰ Expira el {new Date(fechaCaducidad).toLocaleDateString()}{" "}
                            {new Date(fechaCaducidad) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                              ? "(Pronto expira)"
                              : ""}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 px-4">
                  <p className="text-gray-500 text-sm sm:text-base">No hay descuentos disponibles.</p>
                </div>
              )}
            </div>
          </div>

          {/* Ofertas Disponibles */}
          {ofertas.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Ofertas Disponibles</h3>
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {ofertas.length} ofertas activas
                    {selectedOferta && (
                      <span className="ml-2 text-orange-600 font-medium">• 1 seleccionada</span>
                    )}
                  </div>
                  {selectedOferta && (
                    <button
                      onClick={() => setSelectedOferta(null)}
                      className="text-red-500 hover:text-red-700 text-xs underline"
                    >
                      Desmarcar
                    </button>
                  )}
                </div>
              </div>

              <div className={`border border-gray-200 rounded-lg bg-gray-50 ${selectedOferta ? 'max-h-48' : 'max-h-36'} overflow-y-auto`}>
                <div className="p-3 space-y-2">
                  {ofertas.map((oferta) => {
                    const isSelected = selectedOferta?._id === oferta._id;
                    return (
                      <div
                        key={oferta._id}
                        className={`p-3 rounded-lg transition-all duration-200 border ${isSelected
                          ? 'bg-orange-50 border-orange-300 shadow-sm'
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                          }`}
                      >
                        <div className="flex items-start space-x-3">
                          <input
                            type="radio"
                            id={`oferta-${oferta._id}`}
                            name="selectedOferta"
                            checked={isSelected}
                            onChange={() => setSelectedOferta(isSelected ? null : oferta)}
                            className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 flex-shrink-0"
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <label
                                  htmlFor={`oferta-${oferta._id}`}
                                  className="block font-medium text-gray-900 cursor-pointer text-sm sm:text-base truncate"
                                >
                                  {oferta.nombre}
                                </label>
                                <p className="text-xs text-gray-600 mt-1 line-clamp-1">{oferta.descripcion}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className="text-sm font-semibold text-orange-600">
                                    {oferta.tipo === 'DESCUENTO_PORCENTAJE' && `${oferta.porcentajeDescuento}% descuento`}
                                    {oferta.tipo === 'DESCUENTO_FIJO' && `${getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}${oferta.descuentoFijo} descuento`}
                                    {oferta.tipo === '2X1' && '2x1 en productos'}
                                    {oferta.tipo === 'ENVIO_GRATIS' && 'Envío gratis'}
                                  </span>
                                  {oferta.importeMinimo > 0 && (
                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                                      Mín: {getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}{oferta.importeMinimo}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Validación de oferta - más compacta y visible */}
                            {isSelected && (
                              <div className="mt-2 pt-2 border-t border-orange-200">
                                <OfertaValidator
                                  ofertaId={oferta._id}
                                  clienteId={clienteId}
                                  importeCompra={montoConsumido}
                                  onValidation={setOfertaValidation}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Monto Consumido y Total con Descuento */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Resumen de Montos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto Consumido</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={montoConsumido === 0 ? '' : montoConsumido}
                  onChange={handleMontoChange}
                  onBlur={(e) => {
                    // Asegurar que siempre tenga máximo 2 decimales al perder el foco
                    const numValue = Number(e.target.value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      const roundedValue = Math.round(numValue * 100) / 100;
                      setMontoConsumido(roundedValue);
                    } else {
                      setMontoConsumido(0);
                    }
                  }}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Máximo 2 decimales
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Total con Descuento</label>
                <div className="px-3 py-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-lg font-bold text-green-600">
                    {getCurrencySymbol(marca?.pointsRules?.currency || 'USD')}{totalWithDiscount.toFixed(2)}
                  </p>
                  {selectedOferta && ofertaValidation?.valida && (
                    <p className="text-xs text-orange-600 mt-1">
                      Oferta aplicada: {selectedOferta.nombre}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Puntos a Acumular */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Puntos a Acumular</h3>

            {/* Motor de Puntos Automático - Solo si está configurado */}
            {marca && marca.pointsEngineVersion === 1 && marca.pointsRules && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-3">
                  <FaCog className="mr-2 text-blue-600" />
                  <h4 className="text-sm font-medium text-blue-800">Cálculo Automático</h4>
                </div>

                <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Puntos calculados:</span>
                    <div className="flex items-center space-x-2">
                      {(isCalculating || loadingPreview) ? (
                        <FaSpinner className="w-4 h-4 animate-spin text-blue-500" />
                      ) : (
                        <span className="text-lg font-bold text-blue-600">
                          {calculatedPoints} puntos
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Basado en {marca.pointsRules.mode === 'RATE_PER_AMOUNT'
                      ? `${marca.pointsRules.ratePerAmount?.rate || 1} punto(s) por cada ${marca.pointsRules.ratePerAmount?.step || 1}${getCurrencySymbol(marca.pointsRules.currency || 'EUR')}`
                      : marca.pointsRules.mode === 'TIERED'
                        ? 'sistema por niveles'
                        : 'multiplicadores por categoría'
                    }
                  </p>
                </div>

                {/* Información de límites para PUNTOS */}
                {tipoFidelizacion === 'PUNTOS' && puntosDisponiblesTicket !== Infinity && (
                  <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Puntos disponibles:</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {puntosDisponiblesTicket} / {puntosMaximos}
                      </span>
                    </div>
                  </div>
                )}

                {/* Advertencia si se perderán puntos */}
                {tipoFidelizacion === 'PUNTOS' && tieneLimiteAplicado() && puntosPerdidosHook() > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start">
                      <span className="text-yellow-600 mr-2">⚠️</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">
                          Se ajustarán a {puntosDisponiblesTicket} puntos (límite del ticket)
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Se perderán {puntosPerdidosHook()} puntos por superar el límite
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-xs text-blue-600 mb-3">
                  💡 Puedes modificar el valor calculado si lo deseas
                </p>
              </div>
            )}

            {/* Input de puntos (siempre visible) */}
            {tipoFidelizacion === 'PUNTOS' ? (
              marca?.pointsEngineVersion === 1 && marca?.pointsRules ? (
                // Para PUNTOS con motor activo, mostrar input numérico con límite
                <div>
                  <input
                    type="number"
                    value={selectedPoints}
                    onChange={handlePointsChange}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                    min="1"
                    max={puntosDisponiblesTicket !== Infinity ? puntosDisponiblesTicket : undefined}
                  />
                  {puntosDisponiblesTicket !== Infinity && (
                    <p className="text-xs text-gray-500 mt-1">
                      Máximo: {puntosDisponiblesTicket} puntos disponibles de {puntosMaximos} totales
                    </p>
                  )}
                </div>
              ) : (
                // Para PUNTOS sin motor, usar dropdown (comportamiento existente)
                <select
                  value={selectedPoints}
                  onChange={handlePointsChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                >
                  {puntosDisponibles.map((punto, index) => (
                    <option key={index} value={punto}>
                      {punto} puntos
                    </option>
                  ))}
                </select>
              )
            ) : (
              <input
                type="number"
                value={selectedPoints}
                onChange={handlePointsChange}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                min="1"
              />
            )}

            {/* Indicador si el usuario modificó los puntos */}
            {marca && marca.pointsEngineVersion === 1 && marca.pointsRules && userModifiedPoints && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-700">
                  ⚠️ Has modificado el valor calculado automáticamente
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer del modal - Siempre visible */}
        <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium text-sm"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleAccumulate}
              disabled={
                (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1 && marca?.pointsRules
                  ? selectedPoints > puntosDisponiblesTicket
                  : tipoFidelizacion === 'PUNTOS' && selectedPoints > puntosDisponibles.length) ||
                montoConsumido <= 0 ||
                (selectedOferta && !ofertaValidation?.valida) ||
                (tipoFidelizacion === 'PUNTOS' && ticketCompleto())
              }
            >
              Acumular Puntos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccumulatePointsModal;
